import { useState, useEffect, useRef } from "react";
import { useParams } from "wouter";
import { useToast } from '../hooks/use-toast';
import { 
  Card, 
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { 
  Download, 
  FileSignature, 
  Building, 
  Check, 
  X, 
  Info, 
  Phone,
  Mail,
  FileText,
  MapPin
} from "lucide-react";
import { apiRequest } from '../lib/queryClient';
import { generatePdfHtml, PdfTemplateConfig, DEFAULT_PDF_TEMPLATE_CONFIG } from '../components/pdf/unified-pdf-template';
import { formatPhone } from '../lib/validation';

// Componente para el pad de firma
const SignaturePad = ({ onSave, onCancel }: { onSave: (signatureData: string) => void, onCancel: () => void }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Configurar el canvas
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    
    // Limpiar canvas
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
  }, []);
  
  const getCoordinates = (event: MouseEvent | TouchEvent): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    let clientX: number, clientY: number;
    
    if ('touches' in event) {
      // Es un evento táctil
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      // Es un evento de ratón
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };
  
  const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    
    const coords = getCoordinates(event.nativeEvent);
    if (!coords) return;
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };
  
  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    
    const coords = getCoordinates(event.nativeEvent);
    if (!coords) return;
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setIsEmpty(false);
  };
  
  const stopDrawing = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    
    ctx.closePath();
    setIsDrawing(false);
  };
  
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };
  
  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const signatureData = canvas.toDataURL('image/png');
    onSave(signatureData);
  };
  
  return (
    <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-500">Draw your signature below:</p>
      <div className="border rounded-lg p-1 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
        <canvas
          ref={canvasRef}
          width={500}
          height={200}
          className="w-full h-[200px] cursor-crosshair border border-dashed border-gray-300 rounded touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={clearCanvas}>
          Borrar
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button onClick={saveSignature} disabled={isEmpty}>
            Firmar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Componente principal
export default function PublicInvoiceView() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [pdfConfig, setPdfConfig] = useState<PdfTemplateConfig | null>(null);
  const [contractorLogoUrl, setContractorLogoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const { toast } = useToast();

  // Cargar los datos de la factura
  useEffect(() => {
    const fetchInvoiceData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Obtener la factura con todos los datos relacionados
        // Use fetch directly instead of apiRequest to have better error control
        const invoiceRes = await fetch(`/api/public/invoices/${id}`, {
          method: 'GET',
          credentials: 'include',
        });
        
        // Check if response is OK
        if (!invoiceRes.ok) {
          // Try to get error message from response
          const contentType = invoiceRes.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await invoiceRes.json();
            throw new Error(errorData.error || errorData.message || `Error al cargar la factura: ${invoiceRes.status}`);
          } else {
            // HTML error page or other non-JSON response
            throw new Error(`Error al cargar la factura: ${invoiceRes.status} ${invoiceRes.statusText}`);
          }
        }
        
        // Check content type before parsing as JSON
        const contentType = invoiceRes.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          // If not JSON, read as text to get error details
          const text = await invoiceRes.text();
          console.error('Non-JSON response:', text.substring(0, 200));
          throw new Error(`Error al cargar la factura: El servidor devolvió una respuesta no válida`);
        }
        
        const invoiceData = await invoiceRes.json();
        
        // Extraer los datos de la respuesta
        const { items: invoiceItems, client: clientData, project: projectData, ...invoiceDetails } = invoiceData;
        
        // Actualizar el estado con los datos recibidos
        setInvoice(invoiceDetails);
        setClient(clientData);
        if (projectData) {
          setProject(projectData);
        }
        setItems(invoiceItems || []);
        
        // Fetch contractor's PDF template config and logo (non-critical; use default if fails)
        if (invoiceData.contractorId) {
          try {
            const settingsResponse = await fetch(`/api/public/contractor/${invoiceData.contractorId}/settings`);
            if (settingsResponse.ok) {
              const contentType = settingsResponse.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const text = await settingsResponse.text();
                try {
                  const settingsData = JSON.parse(text);
                  if (settingsData?.pdfTemplate) {
                    setPdfConfig(settingsData.pdfTemplate);
                  }
                  if (settingsData?.logoUrl) {
                    setContractorLogoUrl(settingsData.logoUrl);
                  }
                } catch {
                  // Response claimed JSON but wasn't (e.g. HTML error page) - use default
                }
              }
            }
          } catch {
            // Network error or fetch failed - use default config silently
          }
        }
        
      } catch (err: any) {
        console.error("Error:", err);
        setError(err.message || "Error al cargar los datos de la factura");
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchInvoiceData();
    }
  }, [id]);

  // Función para manejar la firma de la factura
  const handleSignatureSubmit = async (signatureData: string) => {
    if (!invoice || !id) return;
    
    try {
      setSignatureDialogOpen(false);
      
      const response = await apiRequest("POST", `/api/public/invoices/${id}/client-action`, {
        action: "sign",
        signature: signatureData,
        notes: "Signed by client through portal"
      });
      
      if (!response.ok) {
        throw new Error("Error saving signature");
      }
      
      // Obtener los datos actualizados de la factura
      const updatedInvoiceRes = await apiRequest("GET", `/api/public/invoices/${id}`);
      if (updatedInvoiceRes.ok) {
        const updatedData = await updatedInvoiceRes.json();
        const { items: invoiceItems, client: clientData, project: projectData, ...invoiceDetails } = updatedData;
        
        // Actualizar el estado local con los datos recibidos
        setInvoice(invoiceDetails);
        setItems(invoiceItems || []);
      } else {
        // Actualizar solo el estado local si no se pudo obtener la factura actualizada
        setSignatureData(signatureData);
        setInvoice({
          ...invoice,
          status: "signed",
          signature: signatureData,
          signedAt: new Date().toISOString(),
        });
      }
      
      toast({
        title: "Invoice signed successfully",
        description: "Thank you for your signature.",
      });
      
    } catch (error: any) {
      console.error("Error al firmar la factura:", error);
      toast({
        title: "Error signing invoice",
        description: error.message || "Por favor, inténtelo de nuevo.",
        variant: "destructive",
      });
    }
  };

  // Función para descargar la factura como PDF
  const handleDownloadInvoice = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const config = pdfConfig ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...pdfConfig } : DEFAULT_PDF_TEMPLATE_CONFIG;

    const htmlContent = generatePdfHtml({
      data: {
        ...invoice,
        items: items,
        client: client,
        contractor: {
          companyName: invoice.contractorCompanyName || 'REMODRA',
          logoUrl: contractorLogoUrl || null
        }
      },
      config,
      type: 'invoice',
      isSigned: !!invoice.clientSignature
    });

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog (user can Save as PDF)
    setTimeout(() => {
      printWindow.print();
    }, 250);
    
    toast({
      title: "PDF Download",
      description: "Print dialog opened. Select 'Save as PDF' to download.",
    });
  };

  // Renderizar pantalla de carga
  if (loading) {
    return (
      <div className="container mx-auto p-4 flex flex-col items-center justify-center min-h-[80vh]">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mb-4"></div>
        <p className="text-gray-500">Cargando factura...</p>
      </div>
    );
  }

  // Renderizar mensaje de error
  if (error) {
    return (
      <div className="container mx-auto p-4">
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
            <CardDescription>
              No se pudo cargar la factura solicitada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
          </CardContent>
          <CardFooter>
            <Button variant="secondary" onClick={() => window.history.back()}>
              Volver
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Renderizar mensaje si no hay factura
  if (!invoice || !client) {
    return (
      <div className="container mx-auto p-4">
        <Card>
          <CardHeader>
            <CardTitle>Factura no encontrada</CardTitle>
            <CardDescription>
              No se pudo encontrar la factura solicitada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p>La factura solicitada no existe o ha sido eliminada.</p>
          </CardContent>
          <CardFooter>
            <Button variant="secondary" onClick={() => window.history.back()}>
              Volver
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Calcular el total
  const subtotal = items.reduce((acc, item) => acc + (item.quantity * item.unitPrice), 0);
  const taxRate = invoice.taxRate || 0;
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  // Renderizar la vista de la factura
  return (
    <div className="container mx-auto p-4 md:p-6 max-w-5xl">
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              {invoice.contractorLogoUrl && (
                <div className="mb-3">
                  <img 
                    src={invoice.contractorLogoUrl} 
                    alt={invoice.contractorName || "Company"} 
                    className="max-h-16 max-w-40 object-contain"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <Building className="h-5 w-5 text-primary" />
                <span className="text-lg font-medium text-primary">
                  {invoice.contractorName || "ContractorHub"}
                </span>
              </div>
              <CardTitle className="text-xl md:text-2xl font-semibold">
                Factura #{invoice.invoice_number || invoice.invoiceNumber || invoice.id}
              </CardTitle>
            </div>
            <div className="flex flex-col items-start md:items-end mt-4 md:mt-0">
              <StatusBadge status={invoice.status} />
              <p className="text-sm text-gray-500 mt-2">
                Issue Date: {format(new Date(invoice.createdAt), "PP", { locale: es })}
              </p>
              {invoice.dueDate && (
                <p className="text-sm text-gray-500">
                  Due Date: {format(new Date(invoice.dueDate), "PP", { locale: es })}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Información de cliente y contratista */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-700">Bill To:</h3>
              <p className="font-medium">{client.firstName} {client.lastName}</p>
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{client.address}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{client.email}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span>{formatPhone(client.phone)}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-700">Contractor:</h3>
              <p className="font-medium">{invoice.contractorName || "ContractorHub"}</p>
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{invoice.contractorAddress || "Contractor Address"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{invoice.contractorEmail || "contractor@email.com"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span>{invoice.contractorPhone || "(555) 123-4567"}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Detalles del proyecto */}
          {project && (
            <div className="bg-gray-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <h3 className="font-semibold">Project Details:</h3>
              </div>
              <p className="font-medium">{project.title}</p>
              <p className="text-sm text-gray-600 mt-1">{project.description}</p>
            </div>
          )}
          
          {/* Tabla de conceptos */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-3">Details:</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50%]">Description</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {item.description}
                      {item.notes && (
                        <p className="text-sm text-gray-500 mt-1">{item.notes}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">${item.unitPrice.toFixed(2)}</TableCell>
                    <TableCell className="text-right">${(item.quantity * item.unitPrice).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {/* Totales */}
          <div className="flex flex-col items-end space-y-2 pt-2">
            <div className="w-full max-w-xs">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              {taxRate > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Tax ({taxRate}%):</span>
                  <span className="font-medium">${taxAmount.toFixed(2)}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between py-2">
                <span className="font-bold">Total:</span>
                <span className="font-bold text-lg">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          {/* Términos y condiciones */}
          <div className="text-sm text-gray-600 border-t pt-4">
            <h3 className="font-semibold text-gray-700 mb-2">Terms and Conditions:</h3>
            <p>{invoice.terms || "Payment must be made within the established period. Please include the invoice number in your payment."}</p>
          </div>
          
          {/* Firma */}
          {invoice.status === 'signed' && invoice.signature && (
            <div className="border rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-gray-700">Signed by client:</h3>
              <div className="p-2 bg-white border rounded-lg">
                <img 
                  src={invoice.signature} 
                  alt="Client signature" 
                  className="max-h-24 mx-auto" 
                />
              </div>
              <p className="text-sm text-gray-500 text-center">
                Signed on {format(new Date(invoice.signedAt), "PPpp")}
              </p>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={handleDownloadInvoice}>
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
          
          {invoice.status === 'pending' && (
            <div className="flex gap-3">
              <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <FileSignature className="mr-2 h-4 w-4" />
                    Sign Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px]">
                  <DialogHeader>
                    <DialogTitle>Sign Invoice</DialogTitle>
                    <DialogDescription>
                      By signing this invoice, you confirm that you have received the goods or services described.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <SignaturePad 
                    onSave={handleSignatureSubmit}
                    onCancel={() => setSignatureDialogOpen(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}

// Componente para mostrar el estado de la factura
function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'draft':
      return <Badge variant="outline">Draft</Badge>;
    case 'pending':
      return <Badge className="bg-green-500">Pending signature</Badge>;
    case 'signed':
      return <Badge className="bg-blue-600">Signed</Badge>;
    case 'paid':
      return <Badge className="bg-green-600">Paid</Badge>;
    case 'overdue':
      return <Badge variant="destructive">Overdue</Badge>;
    case 'cancelled':
      return <Badge variant="outline" className="text-red-600 border-red-300">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}